import React, { useMemo, useRef, useState } from 'react';
import {
  ImperativePanelHandle,
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from 'react-resizable-panels';
import { throttle } from 'lodash';

import useHotkeys from '@lyearn/core/hooks/useHotkeys';
import useScreen from '@lyearn/core/hooks/useScreen';
import { Trans } from '@lyearn/i18n';
import { ArrowheadRightOutline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { IconButton, Stack, Tooltip } from '@lyearn/ui';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import useBodyHeightStyles from '@/hooks/useBodyHeightStyles';
import useWindowSize from '@/hooks/useWindowSize';

import { overflowUnset, topZero } from './styles';

interface SidebarPanelProps {
  Sidebar?: React.ReactNode;
  Content?: React.ReactNode;
  // minSidebarWidth?: number; // px
  autoSaveId?: string;
  className?: string;
  mainPanelClassName?: string; // Class name for the main panel
  fixedWidth?: number; // Optional fixed width for the sidebar in px
}

const DEFAULT_SIDEBAR_WIDTH = 250;
export const MIN_SIDEBAR_WIDTH = 180;
const MAX_SIDEBAR_WIDTH = 250;
const UNIFIED_SIDEBAR_WIDTH = 255;

export default function SidebarPanel(props: SidebarPanelProps) {
  const { Sidebar, Content, autoSaveId, className, mainPanelClassName, fixedWidth } = props;
  const { isMobile } = useScreen();
  const panelRef = useRef<ImperativePanelHandle>(null);
  const [collapsed, setCollapsed] = useState(false);
  const { topStickyPositionStyles } = useBodyHeightStyles();
  const { [FeatureFlags.EnableUnifiedDashboard]: isUnifiedDashboardEnabled } = useFeatureFlags();
  const sidebarPanelTopStyles = isUnifiedDashboardEnabled ? topZero : topStickyPositionStyles;

  const { width } = useWindowSize();

  // Calculate percent for fixedWidth if provided
  const fixedWidthPercent = useMemo(
    () => (fixedWidth ? (fixedWidth * 100) / width : undefined),
    [fixedWidth, width],
  );

  // Cap minSizePercent at 10% to ensure proper collapse behavior
  // react-resizable-panels auto-collapses when size < minSize/2
  // So minSize of 10% means it collapses when dragged below 5%
  const defaultWidth = isUnifiedDashboardEnabled ? UNIFIED_SIDEBAR_WIDTH : DEFAULT_SIDEBAR_WIDTH;
  const maxWidth = isUnifiedDashboardEnabled ? UNIFIED_SIDEBAR_WIDTH : MAX_SIDEBAR_WIDTH;

  const defaultSizePercent = useMemo(
    () => Math.min((defaultWidth * 100) / width, 20),
    [defaultWidth, width],
  );
  const minSizePercent = useMemo(() => Math.min((MIN_SIDEBAR_WIDTH * 100) / width, 10), [width]);
  const maxSizePercent = useMemo(() => Math.min((maxWidth * 100) / width, 25), [maxWidth, width]);

  // Track resize to detect when panel expands back
  const onPanelResize = useMemo(
    () =>
      throttle((size: number) => {
        // If collapsed and user is expanding the panel
        if (collapsed && size > 0) {
          setCollapsed(false);
        }
      }, 100),
    [collapsed],
  );

  useHotkeys(
    'l',
    throttle(() => {
      if (collapsed) {
        panelRef.current?.expand();
        panelRef.current?.resize(defaultSizePercent);
        setCollapsed(false);
      } else {
        panelRef.current?.collapse();
        setCollapsed(true);
      }
    }, 300),
  );

  if (!Sidebar || isMobile) {
    return <>{Content}</>;
  }

  return (
    <PanelGroup
      autoSaveId={autoSaveId}
      className={cn(
        className,
        'relative ',
        overflowUnset,
        isUnifiedDashboardEnabled ? '' : 'border-t border-divider',
      )}
      direction="horizontal">
      {/* Collapse/expand button only if not fixedWidth */}
      {!fixedWidth && collapsed ? (
        <Tooltip
          placement="right"
          title={
            <Trans
              components={{
                box: (
                  <Stack
                    alignItems="center"
                    className="inline-flex h-16 w-16 rounded-[0.26rem] bg-[#444]"
                    component="span"
                    justifyContent="center"
                  />
                ),
              }}
              i18nKey="Common:CollapseSidebarDescription"
            />
          }>
          <IconButton
            Icon={ArrowheadRightOutline}
            className="fixed top-[7.2rem] z-[50] translate-x-[1.2rem]"
            size="xl"
            variant="shadow"
            onClick={() => {
              panelRef.current?.expand();
              panelRef.current?.resize(defaultSizePercent);
              setCollapsed(false);
            }}
          />
        </Tooltip>
      ) : null}
      <Panel
        ref={panelRef}
        className={cn('sticky self-start', sidebarPanelTopStyles)}
        collapsible={!fixedWidth}
        defaultSize={fixedWidthPercent ?? defaultSizePercent}
        maxSize={fixedWidthPercent ?? maxSizePercent}
        minSize={fixedWidthPercent ?? minSizePercent}
        order={1}
        style={
          fixedWidth ? { minWidth: fixedWidth, maxWidth: fixedWidth, width: fixedWidth } : undefined
        }
        onCollapse={!fixedWidth ? () => setCollapsed(true) : undefined}
        onResize={!fixedWidth ? onPanelResize : undefined}>
        {Sidebar}
      </Panel>
      {/* Resize handle only if not fixedWidth */}
      {!fixedWidth ? (
        <Tooltip
          followCursor
          placement="right"
          title={
            <Trans
              components={{
                box: (
                  <Stack
                    alignItems="center"
                    className="mt-4 inline-flex h-16 w-16 rounded-[0.26rem] bg-[#444]"
                    component="span"
                    justifyContent="center"
                  />
                ),
                break: <br />,
              }}
              i18nKey="Common:ResizeSidebarDescription"
            />
          }>
          <Stack>
            <PanelResizeHandle className="w-4 border-l border-divider hover:bg-interactive-white-hover" />
          </Stack>
        </Tooltip>
      ) : (
        <Stack className="w-1 border-l border-divider" />
      )}
      <Panel
        className={mainPanelClassName}
        order={2}
        style={{
          overflow: '!auto',
          minWidth: `calc(${width}px - ${fixedWidth ? fixedWidth : maxWidth}px)`,
          overflowY: 'hidden',
        }}>
        {Content}
      </Panel>
    </PanelGroup>
  );
}
